import { useMemo } from 'react'
import { zodResolver } from '@hookform/resolvers/zod'
import { useMutation, useQuery } from '@tanstack/react-query'
import { Big } from 'big.js'
import { SubmitHandler, useForm } from 'react-hook-form'
import { FormattedMessage, useIntl } from 'react-intl'
import { useLocation, useNavigate } from 'react-router'
import { toast } from 'sonner'
import { z } from 'zod'

import { Currency } from '@/constants/currency'
import { BusinessRoute } from '@/constants/paths'
import { queryKeys } from '@/constants/queryKeys'
import { useErrorToast } from '@/hooks/useErrorToast'
import { useSearchParamsValue } from '@/hooks/useSearchParamsValue'
import { formatCurrency, formatMoney, formatRate } from '@/lib/money'
import {
  AmountInput,
  GoBackButton,
  ProgressButton,
  Widget,
} from '@/shared/components'
import { ArrowDown, ArrowTrendUp } from '@/shared/icons/outline'
import {
  Button,
  Form,
  FormField,
  FormItem,
  FullScreen,
  MotionDiv,
  Skeleton,
  SlideInScreen,
  StickyContainer,
  Typography,
} from '@/shared/ui'

import { getRatesWithFees } from '../../api'
import { AccountSelect } from '../../components'
import { useFeature, useWallets } from '../../hooks'
import { Feature } from '../../types'

import { transferMoney } from './api'

const TRANSFER_FORM_ID = 'transfer-form'

const transferSchema = z
  .object({
    fromBalance: z.string(),
    requestId: z.string(),
    fromWallet: z.string().min(1),
    toWallet: z.string().min(1),
    amountIn: z.string().refine((v) => Big(v === '' ? 0 : v).gt(0), {
      message: 'validation.balance.minAmount',
    }),
    amountOut: z.string().optional(),
    currencyIn: z.nativeEnum(Currency),
    currencyOut: z.nativeEnum(Currency).optional(),
    selection: z.enum(['in', 'out']),
  })
  .refine(
    (data) =>
      data.fromWallet
        ? Big(data.fromBalance).gte(data.amountIn === '' ? 0 : data.amountIn)
        : true,
    {
      message: 'validation.balance.insufficient',
      path: ['amountIn'],
    },
  )

type TransferSchema = z.infer<typeof transferSchema>

export const TransferMoney = () => {
  const intl = useIntl()
  const [from] = useSearchParamsValue(['from'])

  const { isEnabled: isEURCWalletEnabled } = useFeature(Feature.EURC_WALLET)

  const notifyError = useErrorToast()

  const { wallets } = useWallets()

  const { mutateAsync, isPending } = useMutation({ mutationFn: transferMoney })

  const navigate = useNavigate()
  const location = useLocation()

  const walletFromParams = useMemo(
    () => wallets?.find((wallet) => wallet.id === from),
    [wallets, from],
  )

  const form = useForm<TransferSchema>({
    mode: 'onChange',
    resolver: zodResolver(transferSchema),

    values: {
      fromBalance: walletFromParams?.balance
        ? Big(walletFromParams.balance).toFixed()
        : Big(0).toFixed(),
      fromWallet: walletFromParams?.id ?? '',
      toWallet: '',
      requestId: '',
      currencyIn: walletFromParams?.currency ?? Currency.USDC,
      currencyOut: undefined,
      amountIn: '',
      amountOut: '',
      selection: 'in',
    },
  })

  const fromCurrency = form.watch('currencyIn')
  const toCurrency = form.watch('currencyOut')

  const ratesQuery = useQuery({
    queryKey: [queryKeys.getFXRates, fromCurrency, toCurrency],
    queryFn: () =>
      getRatesWithFees({ from: fromCurrency, to: toCurrency ?? '' }),
    select: (data) => data.data,
    refetchInterval: 10 * 1000,
    enabled: !!fromCurrency && !!toCurrency && isEURCWalletEnabled,
  })

  const onSubmit: SubmitHandler<TransferSchema> = async (data) => {
    const fromLabel = wallets?.find(
      (wallet) => wallet.id === data.fromWallet,
    )?.label

    const toLabel = wallets?.find(
      (wallet) => wallet.id === data.toWallet,
    )?.label

    const amount = data.selection === 'in' ? data.amountIn : data.amountOut

    const currency =
      data.selection === 'in' ? data.currencyIn : data.currencyOut

    if (!amount || !currency) {
      toast.error('Invalid amount or currency')
      return
    }

    try {
      await mutateAsync({
        requestId: crypto.randomUUID(),
        toWallet: data.toWallet,
        fromWallet: data.fromWallet,
        amount,
        currency,
      })

      toast.success(
        intl.formatMessage({
          defaultMessage: 'Move successful!',
          id: 'transfer.success',
        }),
        {
          description: intl.formatMessage(
            {
              id: 'transfer.success.description',
              defaultMessage:
                'You have successfully moved {amount} {currency} from {from} to {to}',
            },
            {
              amount: formatMoney(amount),
              currency: formatCurrency(currency),
              from: fromLabel,
              to: toLabel,
            },
          ),
        },
      )

      navigate(BusinessRoute.Accounts)
    } catch (error) {
      notifyError(error)
    }
  }

  const showExchange =
    isEURCWalletEnabled &&
    form.watch('currencyOut') !== undefined &&
    form.watch('currencyIn') !== form.watch('currencyOut')

  return (
    <FullScreen>
      <ProgressButton
        progress={0}
        to={location.state?.from ?? BusinessRoute.Dashboard}
      />

      <GoBackButton to={location.state?.from ?? BusinessRoute.Dashboard} />

      <SlideInScreen>
        <Typography text="center" variant="h3">
          <FormattedMessage
            defaultMessage="Move between your accounts"
            id="transfer.title"
          />
        </Typography>

        <div className="p-2" />

        <Typography text="center">
          <FormattedMessage
            defaultMessage="It's instant and it's free"
            id="transfer.subtitle"
          />
        </Typography>

        <div className="p-6" />

        <Form {...form}>
          <form
            id={TRANSFER_FORM_ID}
            className="w-full"
            onSubmit={form.handleSubmit(onSubmit)}
          >
            <Widget
              title={<FormattedMessage defaultMessage="From" id="label.from" />}
              variant="form"
            >
              <FormField
                control={form.control}
                name="fromWallet"
                render={({ field }) => {
                  return (
                    <AccountSelect
                      value={field.value}
                      excludeIds={[form.watch('toWallet')]}
                      label={intl.formatMessage({
                        defaultMessage: 'Select an account',
                        id: 'label.selectAccount',
                      })}
                      onChange={(v) => {
                        field.onChange(v)

                        const selected = wallets?.find(
                          (wallet) => wallet.id === v,
                        )

                        if (selected) {
                          form.setValue(
                            'fromBalance',
                            Big(selected.balance).toFixed(),
                          )

                          form.setValue('currencyIn', selected.currency)

                          if (form.getValues('amountIn') !== '') {
                            form.trigger('amountIn')
                          }
                        }
                      }}
                    />
                  )
                }}
              />
            </Widget>

            <div className="p-3" />

            <Widget
              title={<FormattedMessage defaultMessage="To" id="label.to" />}
              variant="form"
            >
              <FormField
                control={form.control}
                name="toWallet"
                render={({ field }) => {
                  return (
                    <AccountSelect
                      disabled={wallets?.length === 1}
                      value={field.value}
                      excludeIds={[form.watch('fromWallet')]}
                      label={intl.formatMessage({
                        defaultMessage: 'Select an account',
                        id: 'label.selectAccount',
                      })}
                      onChange={(v) => {
                        field.onChange(v)

                        const selected = wallets?.find(
                          (wallet) => wallet.id === v,
                        )

                        if (selected) {
                          form.setValue('currencyOut', selected.currency)
                        }
                      }}
                    />
                  )
                }}
              />
            </Widget>

            <div className="p-3" />

            <Widget
              title={
                <FormattedMessage defaultMessage="Amount" id="label.amount" />
              }
              variant="form"
            >
              <div className="flex flex-col">
                <FormField
                  control={form.control}
                  name="amountIn"
                  render={({ field }) => (
                    <FormItem>
                      <AmountInput
                        currency={form.getValues('currencyIn')}
                        placeholder={
                          showExchange
                            ? intl.formatMessage({
                                defaultMessage: 'Amount to convert',
                                id: 'label.amountToConvert',
                              })
                            : intl.formatMessage({
                                defaultMessage: 'Amount',
                                id: 'label.amount',
                              })
                        }
                        {...field}
                        onChange={(v) => {
                          field.onChange(v)

                          form.setValue('selection', 'in')

                          if (v === '') {
                            form.setValue('amountOut', '')
                          }

                          if (showExchange && v !== '') {
                            const amountOut = ratesQuery.data?.fxRate
                              ? Big(v).mul(ratesQuery.data.fxRate).toFixed(2)
                              : ''

                            form.setValue('amountOut', amountOut)

                            form.trigger('amountOut')
                          }
                        }}
                      />
                    </FormItem>
                  )}
                />

                {showExchange && (
                  <MotionDiv className="flex flex-col">
                    <div className="border-neutral-gray-100 relative z-10 mx-auto -mt-5 -mb-5 flex h-14 w-14 items-center justify-center rounded-full border-[8px] bg-white transition-colors">
                      <ArrowDown className="size-5" />
                    </div>

                    <FormField
                      control={form.control}
                      name="amountOut"
                      render={({ field }) => (
                        <FormItem>
                          <AmountInput
                            currency={form.getValues('currencyOut')}
                            placeholder={intl.formatMessage({
                              defaultMessage: 'Resulting amount',
                              id: 'label.resultingAmount',
                            })}
                            {...field}
                            onChange={(v) => {
                              field.onChange(v)

                              form.setValue('selection', 'out')

                              if (v === '') {
                                form.setValue('amountOut', '')
                              }

                              if (v !== '') {
                                const amountIn = ratesQuery.data?.fxRate
                                  ? Big(v)
                                      .div(ratesQuery.data.fxRate)
                                      .toFixed(2)
                                  : ''

                                form.setValue('amountIn', amountIn)

                                form.trigger('amountIn')
                              }
                            }}
                          />
                        </FormItem>
                      )}
                    />

                    <div className="flex w-full items-center gap-1 pt-2">
                      <ArrowTrendUp />
                      <div className="flex items-center gap-1">
                        <Typography>
                          1 {formatCurrency(fromCurrency)} =
                        </Typography>
                        {ratesQuery.isPending ? (
                          <Skeleton className="h-6 w-24 rounded-md" />
                        ) : (
                          <Typography>
                            {formatRate(ratesQuery.data?.fxRate)}{' '}
                            {formatCurrency(toCurrency)}
                          </Typography>
                        )}
                      </div>
                    </div>
                  </MotionDiv>
                )}
              </div>
            </Widget>
          </form>
        </Form>

        <StickyContainer>
          <Button
            width="full"
            form={TRANSFER_FORM_ID}
            onClick={form.handleSubmit(onSubmit)}
            loading={isPending}
            disabled={isPending || !form.formState.isValid}
            type="submit"
          >
            <FormattedMessage defaultMessage="Move" id="action.transfer" />
          </Button>
        </StickyContainer>
      </SlideInScreen>
    </FullScreen>
  )
}
